import { injectable } from 'inversify';

import { ChipColorUnion } from '@vk-hr-tek/ui/ColoredChip';
import { Filter } from '@vk-hr-tek/core/filter';
import { renderTwoLineOptionEllipsis } from '@vk-hr-tek/ui/input';

import { Attorney, AttorneyCompany } from '@app/gen/attorneys';

import { AttorneyAction, ActionType } from '../../types';

import { AttorneyDetailMapper } from './attorney.detail.mapper';

type StatusType =
  | 'new'
  | 'signed'
  | 'expired'
  | 'revoking'
  | 'revoked'
  | 'canceled';

@injectable()
export class AttorneysListMapper {
  constructor(private readonly attorneyMapper: AttorneyDetailMapper) {}

  private processAttorneyStatus(status: StatusType): {
    statusName: string;
    statusChipColor: ChipColorUnion;
  } {
    switch (status) {
      case 'signed':
        return { statusName: 'Действующая', statusChipColor: 'green' };

      case 'revoking':
        return { statusName: 'В процессе отзыва', statusChipColor: 'orange' };

      case 'new':
        return {
          statusName: 'На подписании',
          statusChipColor: 'blue',
        };

      case 'canceled':
        return { statusName: 'Отключена', statusChipColor: 'purple' };

      case 'revoked':
        return { statusName: 'Отозвана', statusChipColor: 'red' };

      case 'expired':
        return { statusName: 'Истекла', statusChipColor: 'gray' };
    }
  }

  processSelected(attroneyActions: AttorneyAction[][]): AttorneyAction[] {
    const actionTypes: Record<ActionType, AttorneyAction | null> = {
      unrevoke: null,
      delete: null,
      cancel: null,
      revoke: null,
      signRevoke: null,
      signAttorney: null,
      export: null,
    };

    attroneyActions.forEach((actions) => {
      actions.forEach((action) => {
        actionTypes[action.type] = {
          ...action,
          attorneys: [
            ...(actionTypes[action.type]?.attorneys || []),
            ...action.attorneys,
          ],
        };
      });
    });

    return Object.values(actionTypes).filter(function (
      actionType: AttorneyAction | null,
    ): actionType is AttorneyAction {
      return !!actionType;
    });
  }

  processTooltip(status: StatusType) {
    if (status === 'new') {
      return 'Необходимо подписать';
    }

    if (status === 'revoking') {
      return 'Необходимо подписать отзыв';
    }

    return '';
  }

  processAttorneysList(attorneys: Attorney[], companies: AttorneyCompany[]) {
    return attorneys.map((attorney) => ({
      authorFullName: attorney.author_full_name,
      authorUserId: attorney.author_user_id,
      creationDate: attorney.creation_date,
      fullName: attorney.full_name,
      isDefault: attorney.is_default,
      userId: attorney.user_id,
      validFrom: attorney.valid_from,
      validTo: attorney.valid_to,
      company: attorney.company,
      id: attorney.id,
      imported: attorney.imported,
      issuer: attorney.issuer,
      powers: attorney.powers,
      tooltip: this.processTooltip(attorney.status),
      status: attorney.status,
      cryptoType: attorney.company.crypto_pro_plugin_open_type,
      actions: this.attorneyMapper.processActions(attorney, {
        canSign:
          !!companies.length &&
          !!companies.find(
            (company) => company.company.id === attorney.company.id,
          )?.can_sign,
      }),
      ...this.processAttorneyStatus(attorney.status),
    }));
  }

  processFilters(filters: Filter[] | null) {
    if (!filters) return null;

    return filters?.map((filter) => {
      if (filter.key === 'user_id' && filter.type === 'search') {
        return {
          ...filter,
          minInputValueLength: 3,
          renderOption: renderTwoLineOptionEllipsis,
        };
      }

      return filter;
    });
  }
}
